import React, { useCallback, useEffect, useState } from 'react'
import { i18n } from '@fs/zion-locale'
import { Trans } from 'react-i18next/icu.macro'
import { css } from '@emotion/core'
import ZionMap, { Boundary, Marker } from '@fs/zion-map'
import { appConfig } from '@fs/zion-config'

import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import styles from './SearchResultsPageMap.styles'

const { GOOGLE_MAPS_API_KEY } = appConfig

const UNKNOWN_PLACE_ID = '-1'

const cssTooltip = css`
  font-size: 14px;
  pointer-events: none;
  text-align: center;
`

const cssTooltipTitle = css`
  &:not(:empty) {
    line-height: 1.25em;
    padding: 0.5em;
    padding-bottom: 0;
  }

  &:not(:empty) + p {
    font-size: 12px;
    line-height: 1em;
    padding: 0.5em;
    padding-block-start: 0;
  }
`

const cssTooltipDescription = css`
  line-height: 1em;
`

const cssLoading = css`
  background: rgba(0, 0, 0, 0.4);
  height: 100%;
  inset-inline-start: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;

  > div {
    color: white;
    position: absolute;
    z-index: 2;
  }
`

export default function SearchResultsPageMap({
  hoveredResultWhereFacet,
  loading: searchLoading,
  onMouseOutMarker,
  onMouseOverMarker,
  places,
  searchCriteria,
  searchResults,
  updateSearchCriteria,
}) {
  const { place, volumeSetSearch } = searchCriteria

  const [boundaries, setBoundaries] = useState()
  const [markers, setMarkers] = useState()
  const [isLoading, setIsLoading] = useState(true)

  const unknownTotalCount = searchResults?.unknownTotalCount || searchResults?.parentPlaceUnknownTotalCount
  const whereFacets = searchResults?.whereFacets
  const placesLength = places?.length
  const hoveredResultWhereFacetPlaceId = hoveredResultWhereFacet?.place?.id

  const onMouseOver = useCallback(
    ({ whereFacet }) => {
      onMouseOverMarker?.(Number(whereFacet.place.id))
    },
    [onMouseOverMarker]
  )

  const onMouseOut = useCallback(() => {
    onMouseOutMarker?.(null)
  }, [onMouseOutMarker])

  // Set markers and single unknown marker (if any) when places changes
  useEffect(() => {
    if (whereFacets && !searchLoading) {
      const whereFacetMarkers = whereFacets.map((whereFacet) => {
        if (
          !whereFacet.count ||
          !whereFacet.place ||
          whereFacet.place?.latitude === undefined ||
          whereFacet.place?.longitude === undefined
        ) {
          return null
        }

        const mouseOver = () => {
          onMouseOver({
            whereFacet,
          })
        }
        const placeType = whereFacet.place?.localizedType ?? whereFacet.place.type
        return (
          <Marker
            ariaLabel={whereFacet.place.fullName || whereFacet.place.name}
            chars={whereFacet.count}
            color={whereFacet.place.id === hoveredResultWhereFacetPlaceId ? 'green' : 'red'}
            focused={whereFacet.place.id === hoveredResultWhereFacetPlaceId}
            key={whereFacet.place.id}
            label={
              <div css={cssTooltip}>
                <h2 css={cssTooltipTitle}>{whereFacet.place.fullName || whereFacet.place.name}</h2>
                <p css={cssTooltipDescription}>
                  {placeType}
                  {placeType ? ',' : ''}{' '}
                  <Trans
                    components={[]}
                    defaults="{count, plural, =0 {# collections} one {# collection} other {# collections}}"
                    i18nKey="searchResultsPage.tooltip.collections"
                    values={{
                      count: whereFacet.count,
                    }}
                  />
                </p>
              </div>
            }
            latitude={whereFacet.place.latitude}
            longitude={whereFacet.place.longitude}
            metadata={{
              placeId: whereFacet.place.id,
              testId: `marker-${whereFacet.place.fullName}`,
            }}
            onBlur={onMouseOut}
            onClick={() => {
              if (whereFacet.place.id !== 'result') {
                updateSearchCriteria({ place: whereFacet.place.id })
              }
            }}
            onFocus={mouseOver}
            onMouseEnter={mouseOver}
            onMouseLeave={onMouseOut}
          />
        )
      })

      let unknownMarker = []
      if (unknownTotalCount) {
        let unknownPlace = places?.[0]

        if (!unknownPlace) {
          const PREVENT_MARKER_OVERLAP = 0.01

          unknownPlace = {
            ...whereFacets[0].place,
            latitude: whereFacets[0].place.latitude + PREVENT_MARKER_OVERLAP,
            longitude: whereFacets[0].place.longitude + PREVENT_MARKER_OVERLAP,
          }
        }

        if (unknownPlace.latitude !== undefined && unknownPlace.longitude !== undefined) {
          const mouseOver = () => {
            onMouseOver({
              whereFacet: {
                count: unknownTotalCount,
                place: {
                  ...unknownPlace,
                  id: UNKNOWN_PLACE_ID,
                  name: i18n.t('placesWithin.unknownResults', 'Unknown'),
                  fullName: i18n.t('placesWithin.unknownResults', 'Unknown'),
                },
              },
            })
          }

          unknownMarker = [
            <Marker
              ariaLabel={i18n.t('placesWithin.unknownResults', 'Unknown')}
              chars={unknownTotalCount}
              color={UNKNOWN_PLACE_ID === `${hoveredResultWhereFacetPlaceId}` ? 'green' : 'yellow'}
              focused={UNKNOWN_PLACE_ID === `${hoveredResultWhereFacetPlaceId}`}
              key={UNKNOWN_PLACE_ID}
              label={
                <div css={cssTooltip}>
                  <h2 css={cssTooltipTitle}>{i18n.t('placesWithin.unknownResults', 'Unknown')}</h2>
                  <p css={cssTooltipDescription}>
                    {i18n.t('placesWithin.unknownResults', 'Unknown')}
                    {', '}
                    <Trans
                      components={[]}
                      defaults="{count, plural, =0 {# collections} one {# collection} other {# collections}}"
                      i18nKey="searchResultsPage.tooltip.collections"
                      values={{
                        count: unknownTotalCount,
                      }}
                    />
                  </p>
                </div>
              }
              latitude={unknownPlace.latitude}
              longitude={unknownPlace.longitude}
              metadata={{
                placeId: UNKNOWN_PLACE_ID,
                testId: `marker-${i18n.t('placesWithin.unknownResults', 'Unknown')}`,
              }}
              onBlur={onMouseOut}
              onClick={() => {
                if (unknownPlace.id !== 'result') {
                  updateSearchCriteria({ showUnknown: true })
                }
              }}
              onFocus={mouseOver}
              onMouseEnter={mouseOver}
              onMouseLeave={onMouseOut}
            />,
          ]
        }
      }

      const finalMarkers = [...whereFacetMarkers, ...unknownMarker]

      setMarkers(finalMarkers)
    } else if (!searchLoading) {
      setMarkers([])
    }
  }, [
    hoveredResultWhereFacetPlaceId,
    onMouseOut,
    onMouseOver,
    places,
    searchLoading,
    unknownTotalCount,
    updateSearchCriteria,
    whereFacets,
  ])

  // Set boundaries when places changes
  useEffect(() => {
    if (place === undefined || placesLength > 1) {
      setBoundaries([])
      return
    }

    if (whereFacets && !searchLoading) {
      setBoundaries(
        places?.map((currentPlace) => {
          return <Boundary key={currentPlace.id} placeId={Number(currentPlace.id)} />
        }) || []
      )
    } else if (!searchLoading) {
      setBoundaries([])
    }
  }, [place, places, placesLength, searchLoading, whereFacets])

  useEffect(() => {
    let canSet = true

    if (searchLoading && !isLoading) {
      setIsLoading(true)
    } else if (!searchLoading && isLoading) {
      setTimeout(() => {
        canSet && setIsLoading(false)
      }, 100)
    }

    return () => {
      canSet = false
    }
  }, [isLoading, searchLoading])

  let initialLatitude = markers?.[0]?.props.latitude
  let initialLongitude = markers?.[0]?.props.longitude
  let initialZoom
  if (volumeSetSearch === 'jiapu') {
    // China centroid
    initialLatitude = 38.073255
    initialLongitude = 104.691139
    initialZoom = 4
  } else if (volumeSetSearch === 'jokbo') {
    // South Korea centroid
    initialLatitude = 36.402387
    initialLongitude = 127.762246
    initialZoom = 6
  }
  return (
    <div css={styles.map} role="region" aria-label="Search results map">
      {isLoading && (
        <div css={cssLoading} role="alert" aria-busy="true">
          <LoadingSpinner />
        </div>
      )}
      <div css={styles.mapWrapper} role="application" aria-label="Interactive map">
        {boundaries !== undefined && markers !== undefined && (
          <ZionMap
            googleApiKey={GOOGLE_MAPS_API_KEY}
            initialLatitude={initialLatitude}
            initialLongitude={initialLongitude}
            initialZoom={initialZoom}
          >
            {boundaries}
            {markers}
          </ZionMap>
        )}
      </div>
    </div>
  )
}

SearchResultsPageMap.propTypes = {}
